// Shared app WebSocket (/ws): messenger events plus tenant notifications and
// resume-upload events. One socket for all mainLayout pages, kept
// alive with a ping/pong heartbeat and reconnected with exponential backoff.
// Pages subscribe with addListener() instead of opening their own socket.

export const HEARTBEAT_INTERVAL_MS = 15000;
export const PONG_TIMEOUT_MS = 10000;
export const MAX_MISSED_PONGS = 2;
export const RECONNECT_BASE_MS = 1000;
export const RECONNECT_MAX_MS = 30000;

let ws = null;
let active = false; // true between connect() and disconnect()
let hasConnectedBefore = false;
let reconnectAttempts = 0;
let reconnectTimer = null;
let heartbeatTimer = null;
let pongTimer = null;
let missedPongs = 0;
let lastPongAt = null;
let latencyMs = null;

// group -> number of joinGroup() holders. Several components can hold the same
// group, so the server only hears about the first join and the last leave.
// Held groups are re-joined automatically after every reconnect.
const groupRefs = new Map();
const listeners = { open: new Set(), message: new Set(), close: new Set() };

const emit = (event, payload) => {
  listeners[event].forEach((fn) => {
    try { fn(payload); } catch (err) { console.error('Messenger WS listener error:', err); }
  });
};

const buildWsUrl = () => {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_WS_URL;
  if (!token || !baseUrl) return null;
  return `${baseUrl}?token=${encodeURIComponent(token)}`;
};

const isOpen = () => ws?.readyState === WebSocket.OPEN;

// The server closes the socket (1011) on unknown actions or invalid JSON,
// so everything goes out through JSON.stringify.
const sendRaw = (payload) => {
  if (!isOpen()) return false;
  ws.send(JSON.stringify(payload));
  return true;
};

const stopHeartbeat = () => {
  clearInterval(heartbeatTimer);
  clearTimeout(pongTimer);
  heartbeatTimer = null;
  pongTimer = null;
  missedPongs = 0;
};

// Detach handlers before closing so a stalled close handshake (e.g. offline)
// can't delay the reconnect or fire onclose for a socket already replaced.
const dropSocket = () => {
  stopHeartbeat();
  if (!ws) return;
  const old = ws;
  ws = null;
  old.onopen = null;
  old.onmessage = null;
  old.onerror = null;
  old.onclose = null;
  try { old.close(); } catch (_) {}
};

const scheduleReconnect = () => {
  if (!active || reconnectTimer) return;
  const backoff = RECONNECT_BASE_MS * Math.pow(2, reconnectAttempts);
  const jittered = backoff * (0.8 + Math.random() * 0.4);
  const delay = Math.round(Math.min(RECONNECT_MAX_MS, jittered));
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket();
  }, delay);
};

const handleDeadConnection = () => {
  console.warn('Messenger WS: no pong received, reconnecting');
  dropSocket();
  emit('close', { reason: 'heartbeat' });
  scheduleReconnect();
};

const sendPing = () => {
  if (!isOpen()) return;
  if (missedPongs >= MAX_MISSED_PONGS) {
    handleDeadConnection();
    return;
  }
  sendRaw({ action: 'ping', ts: Date.now() });
  missedPongs += 1;
  // Timeout runs from the oldest unanswered ping
  if (!pongTimer) pongTimer = setTimeout(handleDeadConnection, PONG_TIMEOUT_MS);
};

const handlePong = (data) => {
  clearTimeout(pongTimer);
  pongTimer = null;
  missedPongs = 0;
  lastPongAt = Date.now();
  if (typeof data.ts === 'number') latencyMs = lastPongAt - data.ts;
};

const startHeartbeat = () => {
  stopHeartbeat();
  heartbeatTimer = setInterval(sendPing, HEARTBEAT_INTERVAL_MS);
};

function openSocket() {
  if (!active) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  const url = buildWsUrl();
  if (!url) return;

  dropSocket();
  const socket = new WebSocket(url);
  ws = socket;

  socket.onopen = () => {
    const reconnected = hasConnectedBefore;
    hasConnectedBefore = true;
    reconnectAttempts = 0;
    startHeartbeat();
    groupRefs.forEach((_, group) => sendRaw({ action: 'join', group }));
    emit('open', { reconnected });
  };

  socket.onmessage = (ev) => {
    let data;
    try { data = JSON.parse(ev.data); } catch (_) { return; }
    if (!data) return;
    // Pongs stay inside the socket layer
    if (data.type === 'pong') {
      handlePong(data);
      return;
    }
    emit('message', data);
  };

  socket.onerror = () => {
    stopHeartbeat();
  };

  socket.onclose = () => {
    if (ws !== socket) return;
    stopHeartbeat();
    ws = null;
    emit('close', { reason: 'closed' });
    scheduleReconnect();
  };
}

// Tab back in focus / network back: check the socket now instead of waiting
const handleWake = () => {
  if (!active) return;
  if (isOpen()) {
    sendPing();
    return;
  }
  if (ws?.readyState === WebSocket.CONNECTING) return;
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
  reconnectAttempts = 0;
  openSocket();
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') handleWake();
};

const connect = () => {
  if (active) return;
  active = true;
  hasConnectedBefore = false;
  reconnectAttempts = 0;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleWake);
  openSocket();
};

const disconnect = () => {
  active = false;
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('online', handleWake);
  dropSocket();
  groupRefs.clear();
};

// Every joinGroup() must be paired with one leaveGroup() for the same group
const joinGroup = (group) => {
  if (!group) return;
  const count = groupRefs.get(group) || 0;
  groupRefs.set(group, count + 1);
  // If the socket isn't open yet, onopen joins it
  if (count === 0) sendRaw({ action: 'join', group });
};

const leaveGroup = (group) => {
  const count = groupRefs.get(group);
  if (!count) return;
  if (count > 1) {
    groupRefs.set(group, count - 1);
    return;
  }
  groupRefs.delete(group);
  // If the socket is closed, the server already dropped it from the group
  sendRaw({ action: 'leave', group });
};

// event: 'open' ({ reconnected }) | 'message' (parsed data) | 'close' ({ reason })
const addListener = (event, fn) => {
  listeners[event].add(fn);
  return () => listeners[event].delete(fn);
};

const getStatus = () => ({
  readyState: ws ? ws.readyState : WebSocket.CLOSED,
  lastPongAt,
  latencyMs,
});

const messengerSocket = {
  connect,
  disconnect,
  send: sendRaw,
  joinGroup,
  leaveGroup,
  addListener,
  isOpen,
  getStatus,
};

export default messengerSocket;
